import React, { useState, useRef, useEffect } from 'react';
import Icon from '../../../components/AppIcon';

const TIER_COLORS = {
  platinum: 'bg-purple-100 text-purple-700 border-purple-200',
  gold: 'bg-yellow-100 text-yellow-700 border-yellow-200',
  silver: 'bg-slate-100 text-slate-600 border-slate-200',
  bronze: 'bg-orange-100 text-orange-700 border-orange-200',
};

const EngagementPartnerSwitcher = ({
  partners = [],
  selectedPartnerId,
  selectedPartner,
  onSelectPartner,
  loading = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef?.current && !dropdownRef?.current?.contains(e?.target)) {
        setIsOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filtered = partners?.filter(p =>
    (p?.brand_name || p?.legal_name || '')?.toLowerCase()?.includes(search?.toLowerCase())
  );

  const displayName = selectedPartner?.brand_name || selectedPartner?.legal_name || 'All Partners';
  const tier = selectedPartner?.tier;
  const healthScore = selectedPartner?.health_score || selectedPartner?.enablement_score || null;

  const getHealthColor = (score) => {
    if (score >= 80) return 'text-emerald-600 bg-emerald-50 border-emerald-200';
    if (score >= 50) return 'text-amber-600 bg-amber-50 border-amber-200';
    return 'text-red-500 bg-red-50 border-red-200';
  };

  return (
    <div className="bg-white/95 backdrop-blur-sm border-b border-slate-200 shadow-sm relative z-20">
      <div className="max-w-[1600px] mx-auto px-24 lg:px-32 py-14">
        <div className="flex flex-col lg:flex-row lg:items-center gap-14">

          {/* Label */}
          <div className="flex items-center gap-8 flex-shrink-0">
            <Icon name="Users" size={16} className="text-slate-400" />
            <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Filter by Partner</span>
          </div>

          {/* Dropdown */}
          <div className="relative flex-shrink-0" ref={dropdownRef}>
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="flex items-center gap-12 px-16 py-11 bg-white border-2 border-primary/30 rounded-xl hover:border-primary transition-all min-w-[260px] shadow-sm"
            >
              <div className="p-7 bg-gradient-to-br from-secondary/10 to-primary/10 rounded-lg">
                <Icon name="Building2" size={16} className="text-secondary" />
              </div>
              <div className="flex-1 text-left">
                <div className="text-xs text-slate-400 font-medium leading-none mb-3">Active Partner</div>
                <div className="text-sm font-semibold text-slate-900 truncate max-w-[160px]">
                  {loading ? 'Loading...' : displayName}
                </div>
              </div>
              {tier && (
                <span className={`px-8 py-3 rounded-full text-xs font-semibold capitalize border ${TIER_COLORS?.[tier] || 'bg-slate-100 text-slate-600 border-slate-200'}`}>
                  {tier}
                </span>
              )}
              <Icon name={isOpen ? 'ChevronUp' : 'ChevronDown'} size={16} className="text-slate-400 flex-shrink-0" />
            </button>

            {isOpen && (
              <div className="absolute top-full left-0 mt-8 w-[340px] bg-white border border-slate-200 rounded-xl shadow-xl z-[100] overflow-hidden">
                <div className="p-12 border-b border-slate-100">
                  <div className="relative">
                    <Icon name="Search" size={14} className="absolute left-10 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input
                      type="text"
                      placeholder="Search partners..."
                      value={search}
                      onChange={(e) => setSearch(e?.target?.value)}
                      className="w-full pl-32 pr-12 py-8 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30"
                      autoFocus
                    />
                  </div>
                </div>
                <div className="max-h-[280px] overflow-y-auto">
                  {/* All Partners option */}
                  <button
                    onClick={() => { onSelectPartner(null); setIsOpen(false); setSearch(''); }}
                    className={`w-full flex items-center gap-12 px-16 py-12 hover:bg-slate-50 transition-colors text-left ${!selectedPartnerId ? 'bg-primary/5' : ''}`}
                  >
                    <div className="w-36 h-36 rounded-lg bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center flex-shrink-0">
                      <Icon name="Globe" size={16} className="text-slate-500" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-slate-900">All Partners</div>
                      <div className="text-xs text-slate-500">Show all data</div>
                    </div>
                    {!selectedPartnerId && <Icon name="Check" size={14} className="text-primary flex-shrink-0" />}
                  </button>

                  {filtered?.length === 0 ? (
                    <div className="p-16 text-center text-sm text-slate-500">No partners found</div>
                  ) : (
                    filtered?.map(partner => (
                      <button
                        key={partner?.id}
                        onClick={() => { onSelectPartner(partner?.id); setIsOpen(false); setSearch(''); }}
                        className={`w-full flex items-center gap-12 px-16 py-12 hover:bg-slate-50 transition-colors text-left ${partner?.id === selectedPartnerId ? 'bg-primary/5' : ''}`}
                      >
                        <div className="w-36 h-36 rounded-lg bg-gradient-to-br from-secondary/10 to-primary/10 flex items-center justify-center flex-shrink-0">
                          <span className="text-sm font-bold text-secondary">
                            {(partner?.brand_name || partner?.legal_name || '?')?.charAt(0)?.toUpperCase()}
                          </span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-semibold text-slate-900 truncate">
                            {partner?.brand_name || partner?.legal_name}
                          </div>
                          <div className="text-xs text-slate-500 truncate capitalize">{partner?.partner_type || partner?.type || 'Partner'}</div>
                        </div>
                        {partner?.tier && (
                          <span className={`px-8 py-3 rounded-full text-xs font-semibold capitalize border flex-shrink-0 ${TIER_COLORS?.[partner?.tier] || 'bg-slate-100 text-slate-600 border-slate-200'}`}>
                            {partner?.tier}
                          </span>
                        )}
                        {partner?.id === selectedPartnerId && (
                          <Icon name="Check" size={14} className="text-primary flex-shrink-0" />
                        )}
                      </button>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Quick Stats */}
          {selectedPartner ? (
            <div className="flex flex-wrap items-center gap-10 flex-1">
              {tier && (
                <div className={`flex items-center gap-8 px-12 py-8 rounded-xl border ${TIER_COLORS?.[tier] || 'bg-slate-100 text-slate-600 border-slate-200'}`}>
                  <Icon name="Award" size={14} />
                  <span className="text-xs font-semibold capitalize">{tier} Tier</span>
                </div>
              )}
              {healthScore !== null && (
                <div className={`flex items-center gap-8 px-12 py-8 rounded-xl border ${getHealthColor(healthScore)}`}>
                  <Icon name="Activity" size={14} />
                  <span className="text-xs font-semibold">Health </span>
                  <span className="text-sm font-bold">{healthScore}</span>
                </div>
              )}
              {(selectedPartner?.partner_type || selectedPartner?.type) && (
                <div className="flex items-center gap-8 px-12 py-8 rounded-xl border bg-slate-50 border-slate-200 text-slate-600">
                  <Icon name="Briefcase" size={14} />
                  <span className="text-xs font-semibold capitalize">{selectedPartner?.partner_type || selectedPartner?.type}</span>
                </div>
              )}
              <button
                onClick={() => onSelectPartner(null)}
                className="flex items-center gap-6 px-10 py-8 rounded-xl border border-slate-200 text-slate-500 hover:text-slate-700 hover:border-slate-300 text-xs font-medium transition-all"
              >
                <Icon name="X" size={12} />
                Clear
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-10 px-14 py-10 bg-blue-50 border border-blue-200 rounded-xl text-sm text-blue-700">
              <Icon name="Info" size={15} className="flex-shrink-0" />
              <span className="text-xs font-medium">Showing all partners — select one to filter</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EngagementPartnerSwitcher;
